import { useCallback, useMemo, useRef } from 'react'
import { logFrontendRuntimeEvent } from '../runtimeLogger'
import type { BotProtectionRuntimeEvent, BotProtectionWidgetProps } from './types'
import type { BotProtectionRuntimeState } from './widgetRuntimeState'

const RUNTIME_EVENT_DEDUP_WINDOW_MS = 300

const isDevelopmentHostname = (): boolean => {
  if (typeof window === 'undefined') return false
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.local')
  )
}

type RuntimeEventLoggerInput = Pick<
  BotProtectionWidgetProps,
  'app' | 'containerName' | 'providerId'
> & {
  getRuntimeState: () => BotProtectionRuntimeState
}

export type BotProtectionRuntimeEventLogger = (
  level: BotProtectionRuntimeEvent['level'],
  event: string,
  context?: Record<string, unknown>,
) => void

export const useBotProtectionRuntimeEventLogger = ({
  app,
  containerName,
  providerId,
  getRuntimeState,
}: RuntimeEventLoggerInput): BotProtectionRuntimeEventLogger => {
  const isDevRuntime = useMemo(() => isDevelopmentHostname(), [])
  const lastRuntimeEventRef = useRef<{ key: string; ts: number } | null>(null)

  return useCallback(
    (level, event, context) => {
      if (!isDevRuntime) return
      const payloadContext = {
        providerId,
        runtimeState: getRuntimeState(),
        ...(containerName ? { containerName } : {}),
        ...(context ?? {}),
      }
      const dedupKey = `${level}:${event}:${JSON.stringify(payloadContext)}`
      const now = Date.now()
      const lastEvent = lastRuntimeEventRef.current
      if (
        lastEvent &&
        lastEvent.key === dedupKey &&
        now - lastEvent.ts < RUNTIME_EVENT_DEDUP_WINDOW_MS
      ) {
        return
      }
      lastRuntimeEventRef.current = { key: dedupKey, ts: now }
      logFrontendRuntimeEvent({
        app,
        level,
        event,
        message: 'Bot-protection widget runtime event',
        context: payloadContext,
      })
    },
    [app, containerName, getRuntimeState, isDevRuntime, providerId],
  )
}
